import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IRuleCalculationFields } from 'src/types/priceRules';
import { StatusBar, SnackbarStatus, Typography } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';

import styles from 'src/components/PriceRuleFlowShared/PriceRule.module.scss';
import PriceRuleAdjustmentOptions from '../PriceRuleAdjustmentOptions';
import { RoundingRuleOptions } from '../RoundingRuleOptions';
import PriceRuleOverview from '../PriceRuleFlowShared/PriceRuleOverview';
import {
  buildCalculationSubtitle,
  buildRoundingSubtitle,
  getTitles,
  TPriceRuleTitleStep,
} from 'src/utils/priceRules/confirmationStrings';
import createPriceRules from 'src/helpers/createPriceRules';
import { IAggregator } from 'src/types/aggregator.types';
import {
  createPriceRuleAdjustment,
  createPriceRuleRounding,
  defaultAdjustmentValues,
  defaultRoundingValues,
} from 'src/utils/priceRules/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { CREATE_CHANNEL_RULE_NOTIFICATION, UPDATE_CHANNEL_RULE_NOTIFICATION } from './constants';
import { AdjustmentType, IPriceRule, RoundingType } from 'src/types/price.types';
import { updatePriceRule } from 'src/api/updatePriceRule/updatePriceRule';
import PriceRuleContinueButtons from '../PriceRuleFlowShared/PriceRuleContinueButtons';
import { IntegrationType } from 'src/constants';
import { useRules } from 'src/hooks/useRules/useRules';
import SelectableListWithControls from '../SelectableListWithControls';

type ChannelRuleModalContentProps = {
  channels: IAggregator[];
  closeModal: () => void;
  priceSchemeId: string;
  priceRuleToEdit?: IPriceRule;
  defaultStep?: number;
};

export enum ChannelRuleSteps {
  Channels = 0,
  Adjustment = 1,
  Rounding = 2,
  Confirm = 3,
}

const isContinueButtonDisabled = (
  step: ChannelRuleSteps,
  { rounding, adjustment }: IRuleCalculationFields,
): boolean => {
  switch (step) {
    case ChannelRuleSteps.Adjustment: {
      const adjustmentEnabled = adjustment.type !== AdjustmentType.NONE;
      const noValueExists = Number.isNaN(adjustment.fixed) && Number.isNaN(adjustment.percentage);

      return adjustmentEnabled && noValueExists;
    }
    case ChannelRuleSteps.Rounding: {
      const roundingEnabled = rounding.type !== RoundingType.NONE && typeof rounding.method !== 'undefined';
      const noValueExists = typeof rounding.value === 'undefined' || Number.isNaN(rounding.value);
      return roundingEnabled && noValueExists;
    }
    default:
      return false;
  }
};

const createSteps: TPriceRuleTitleStep[] = [
  { title: 'Select Channel', subtitle: 'Select the channel you would like to create a price automation rule for.' },
  {
    title: 'Channel Calculation',
    subtitle: 'Automate pricing adjustments for this channel based on default product values.',
  },
  {
    title: 'Channel Rounding',
    subtitle: 'Create a rounding rule for all automated price changes within this price band.',
  },
];

const channelToOption = (channel: IAggregator) => ({
  id: channel.aggregatorId,
  label: channel.name,
  subtitle: channel.description || 'Default',
});

const ChannelRuleModalContent = ({
  priceRuleToEdit,
  closeModal,
  priceSchemeId,
  channels = [],
  defaultStep,
}: ChannelRuleModalContentProps) => {
  const queryClient = useQueryClient();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [step, setStep] = useState<number>(defaultStep ?? ChannelRuleSteps.Channels);
  const { data: priceRules = [] } = useRules(priceSchemeId);
  const existingChannelRuleIds = new Set(
    priceRules.filter((rule) => rule.external_system).map((rule) => rule.external_system.id),
  );

  const defaultRounding = defaultRoundingValues(priceRuleToEdit?.rounding);
  const defaultAdjustment = defaultAdjustmentValues(priceRuleToEdit?.adjustment);

  const { handleSubmit, register, formState, setValue, watch, control } = useForm<IRuleCalculationFields>({
    defaultValues: {
      adjustment: defaultAdjustment,
      rounding: defaultRounding,
      entities: [],
    },
  });

  const { errors, isSubmitting } = formState;
  const { entities, rounding, adjustment } = watch();

  const isEditMode = priceRuleToEdit && typeof defaultStep !== 'undefined';
  const showConfirmBtn = step === ChannelRuleSteps.Confirm || isEditMode;
  const showCancelBtn = step === ChannelRuleSteps.Channels || isEditMode;
  const continueDisabled = isContinueButtonDisabled(step, { entities, name, rounding, adjustment });

  const integrations = channels.filter((channel) => {
    return (
      channel.integrationType === IntegrationType.DELIVERY_SERVICE && !existingChannelRuleIds.has(channel.aggregatorId)
    );
  });

  const roundingSubtitle = useMemo(() => buildRoundingSubtitle(rounding), [step]);
  const checkboxOptions = useMemo(() => integrations?.map(channelToOption), [integrations]);
  const calculationSubtitle = useMemo(() => buildCalculationSubtitle(adjustment), [step]);

  const onChannelChange = (channels: string[]) => setValue('entities', channels);

  const decrementStep = () => setStep((s) => s - 1);
  const incrementStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setStep((s) => s + 1);
  };

  const onCreate = async ({ entities, rounding, adjustment }: IRuleCalculationFields) => {
    await createPriceRules(
      {
        entities,
        externalSystems: entities,
        adjustment: createPriceRuleAdjustment(adjustment),
        rounding: createPriceRuleRounding(rounding),
      },
      priceSchemeId,
    );
  };

  const onUpdate = async (fields: IRuleCalculationFields) => {
    await updatePriceRule(
      {
        adjustment: createPriceRuleAdjustment(fields.adjustment),
        rounding: createPriceRuleRounding(fields.rounding),
      },
      priceRuleToEdit.price_rule_id,
      priceSchemeId,
    );
  };

  const onSubmit = async (fields: IRuleCalculationFields) => {
    clearError();
    try {
      if (isEditMode) {
        await onUpdate(fields);
        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: UPDATE_CHANNEL_RULE_NOTIFICATION.SUCCESS,
        });
      } else {
        await onCreate(fields);
        showNotification({
          status: SnackbarStatus.SUCCESS,
          message: CREATE_CHANNEL_RULE_NOTIFICATION.SUCCESS,
        });
      }

      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey.at(1) === QueryKeys.priceBands || queryKey.at(1) === QueryKeys.priceRules,
      });
      closeModal();
    } catch (error) {
      const message = error instanceof Error ? error.message : UPDATE_CHANNEL_RULE_NOTIFICATION.ERROR;

      showNotification({
        status: SnackbarStatus.ERROR,
        message,
      });
    }
  };

  const [title, subtitle] = getTitles(step, createSteps);
  const selectedChannels = channels.filter((c) => entities.includes(c.aggregatorId));

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="channe-rule-form" aria-label="channel-rule-form">
      {!isEditMode && <Typography className={styles['steps']}>{`Step ${step + 1} of 4`}</Typography>}
      <Typography className={styles['title']} size="xxlarge" weight="semi-bold">
        {title}
      </Typography>
      <Typography className={styles['subtitle']}>{subtitle}</Typography>

      {step === ChannelRuleSteps.Channels && (
        <SelectableListWithControls
          options={checkboxOptions}
          onChange={onChannelChange}
          selectedOptions={entities}
          className={styles['selectable-list']}
        />
      )}

      {step === ChannelRuleSteps.Adjustment && (
        <PriceRuleAdjustmentOptions register={register as any} control={control as any} entity="Rule" />
      )}

      {step === ChannelRuleSteps.Rounding && (
        <RoundingRuleOptions register={register as any} control={control as any} errors={errors} />
      )}

      {step === ChannelRuleSteps.Confirm && (
        <PriceRuleOverview
          name="Channel(s)"
          selected={selectedChannels.map((channel) => ({ id: channel.aggregatorId, name: channel.name }))}
          entityType="external_system"
          roundingSubtitle={roundingSubtitle}
          calculationSubtitle={calculationSubtitle}
        />
      )}

      <PriceRuleContinueButtons
        showCancelBtn={showCancelBtn}
        closeModal={closeModal}
        decrementStep={decrementStep}
        showConfirmBtn={showConfirmBtn}
        isSubmitting={isSubmitting}
        continueDisabled={continueDisabled}
        incrementStep={incrementStep}
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export default ChannelRuleModalContent;
